import { ChangeEvent, Dispatch, SetStateAction, useState } from "react"
import { ACSinglePredictDAGRequestModel } from "../../../../api/models"
import InputField from "../../../../components/Form/InputField";
import ConditionalSelectField from "../../../../components/Form/ConditionSelectField";
import SelectField from "../../../../components/Form/SelectFields";
import { classificationMethods, regressionMethods } from "../../../../utils/supplementalData";
import TagsField from "../../../../components/Form/TagsField";
import BooleanField from "../../../../components/Form/BooleanField";

const predTypeOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'conformal-prediction', label: 'Conformal Prediction' }
]

const modelTypeOptions = classificationMethods.concat(regressionMethods).map(method => ({ value: method, label: method }));

const averagingModeOptions = [
    { value: 'All', label: 'All' },
    { value: 'Individual', label: 'Individual' }
];


export default function SetSinglePredictionParameters({
    form,
    setForm,
    showSelect,
    modelTypes,
    setModelTypes,
    numSelectedModels,
}: {
    form: ACSinglePredictDAGRequestModel,
    setForm: Dispatch<SetStateAction<ACSinglePredictDAGRequestModel>>,
    showSelect: boolean,
    modelTypes: string[], 
    setModelTypes: Dispatch<SetStateAction<string[]>>,
    numSelectedModels: number,
}) {
    const [ selectedAveragingMode, setSelectedAveragingMode ] = useState<'All' | 'Individual'>('All');

    const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        })
    };

    const handleSelect = (e: any, name: string) => {
        setForm({
            ...form,
            [name]: Array.isArray(e) ? e.map(val => val.value) : e.value,
        })
    }

    const handleSelectAveragingMode = (e:any, name: string) => {
        const value = e.value as 'All' | 'Individual';
        setSelectedAveragingMode(value);

        setModelTypes([]);
    };

    const handleSelectModels = (e:any, name: string) => {
        const value = e as { value: string, label: string }[];
        setModelTypes(value.map(val => val.value));
    }

    const handleAddTag = (category: string, tag: string) => {
        let addedTag = '';

        if (category === 'general') {
            addedTag = tag;
        } else {
            addedTag = `${category}::${tag}`
        }

        setForm({
            ...form,
            tags: form!.tags!.concat(addedTag)

        })
    }

    const handleRemoveTag = (index: number) => {
        setForm({
            ...form,
            tags: form!.tags!.filter((t, i) => i !== index)
        })
    }

    const handleExplainabilityToggle = (e: ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setForm({
            ...form,
            molecule_highlighting: checked,
            explainability_enabled: checked,
        });
    };


    return (
        <>
            <div className="w-full">
                <InputField 
                    label="Result Name"
                    type='text'
                    id='name'
                    name='name'
                    value={form.name as string}
                    isRequired
                    handleChange={handleInput}
                />
            </div>

            <div className="w-full">
                <ConditionalSelectField label="Averaging Mode" showSelect={showSelect}>
                    <SelectField
                        label="Averaging Mode"
                        id='averaging_mode'
                        name='averaging_mode'
                        options={averagingModeOptions}
                        value={{ value: selectedAveragingMode, label: selectedAveragingMode }}
                        isRequired
                        handleChange={handleSelectAveragingMode}
                        isCreatable={false}
                        isMulti={false}
                        menuPortalTarget={document.getElementById('single-prediction-popup')}
                    />
                </ConditionalSelectField>
            </div>

            {selectedAveragingMode === 'Individual' && (
                <div className="w-full">
                    <ConditionalSelectField label="Models" showSelect={showSelect}>
                        <SelectField
                            label="Models"
                            id='models'
                            name='models'
                            options={modelTypeOptions}
                            value={modelTypes.map(type => ({ value: type, label: type }))}
                            isRequired
                            handleChange={handleSelectModels}
                            isCreatable={false}
                            isMulti
                            menuPortalTarget={document.getElementById('single-prediction-popup')}
                        />
                    </ConditionalSelectField>
                </div>
            )}

            <div className="w-full">
                <ConditionalSelectField label='Prediction Type' showSelect={showSelect}>
                    <SelectField
                        label="Prediction Type"
                        id='prediction_type'
                        name='prediction_type'
                        options={predTypeOptions}
                        value={{ value: form.prediction_type!, label: predTypeOptions.find(opt => opt.value === form.prediction_type!)!.label }}
                        isRequired
                        handleChange={handleSelect}
                        isCreatable={false}
                        isMulti={false}
                        menuPortalTarget={document.getElementById('single-prediction-popup')}
                    />
                </ConditionalSelectField>
            </div>

            <TagsField 
                label="Resultset"
                tags={form.tags! as string[]}
                showSelect={showSelect}
                menuPortalTarget={document.getElementById('single-prediction-popup')}
                handleAddTag={handleAddTag}
                handleRemoveTag={handleRemoveTag}
            />

            <div className="w-full flex flex-col gap-1">
                <BooleanField 
                    label="Run Explainability & Molecule Highlighting"
                    id="explainability"
                    name="explainability"
                    value={form.molecule_highlighting ?? true}
                    handleChange={handleExplainabilityToggle}
                    isRequired={false}
                />
                <p className="text-xs text-gray-500 ml-1">
                    Generates per-atom/bond importance highlighting and SHAP-based feature ranking. May increase prediction time for large workloads.
                </p>
            </div>

            {form.molecule_highlighting && numSelectedModels > 5 && (
                <div className="w-full rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    ⚠️ Explainability on large workloads may take significantly longer.
                    You have <strong>{numSelectedModels}</strong> model families selected.
                    Consider reducing models for faster results.
                </div>
            )}
        </>
    )
}